import { Box, List, ListItem, Typography, useTheme } from "@mui/material";

import DisplayCallRecord from "@/components/FeedItem/DisplayCallRecord";
import DisplayTranscript from "@/components/FeedItem/DisplayTranscript";
import PlayButtonOpenPlayer from "@/components/FeedItem/PlayButtonOpenPlayer";
import HideShowTranscriptButton from "@/components/HideShowTranscriptButton";
import { ampli } from "@/config";
import type { PreferredLanguage } from "@/models/languages";
import type { CallRecord, DisplayArtifact, File, Link as LinkType, Transcription } from "@/store/types";
import { sanitizeHtmlForDisplay } from "@/utils/sanitizeHtmlForDisplay";

type FileWithFragment = File & { fragment: React.ReactNode };
type LinkWithFragment = LinkType & { fragment: React.ReactNode };

type Props = {
  mine: boolean;
  feedItemId: string;
  shouldShowAudioTranscriptContent: boolean;
  hasCallRecord: boolean;
  filesOrLinks: boolean;
  duration: number;
  itemId: string;
  hasPlayableAudio: boolean;
  onPlay: (time: number) => Promise<void>;
  onFinished: () => Promise<void>;
  onPause: (time: number) => Promise<void>;
  displayArtifactRecord: DisplayArtifact | null;
  showModalTranscription: boolean;
  setShowModalTranscription: (value: boolean) => void;
  feedId: string;
  showTranscription: boolean;
  preferredLanguage: PreferredLanguage;
  transcriptions: Transcription[];
  transcriptionContent: Transcription | null;
  unreliableTranscription: boolean;
  unreliableTranscriptLabel: string;
  transcriptTranslatedFrom: string | null;
  hasTranscription: boolean;
  files: FileWithFragment[];
  links: LinkWithFragment[];
  hasDisplayArtifact: (value: string) => void;
  callRecords: CallRecord[];
};

export default function DriverCentricFeedItemContent({
  mine,
  feedItemId,
  shouldShowAudioTranscriptContent,
  hasCallRecord,
  filesOrLinks,
  itemId,
  hasPlayableAudio,
  displayArtifactRecord,
  showModalTranscription,
  setShowModalTranscription,
  feedId,
  showTranscription,
  preferredLanguage,
  transcriptions,
  transcriptionContent,
  unreliableTranscription,
  unreliableTranscriptLabel,
  transcriptTranslatedFrom,
  hasTranscription,
  files,
  links,
  hasDisplayArtifact,
  callRecords,
}: Props) {
  const theme = useTheme();

  return (
    <Box sx={{ display: "flex", width: "100%", gap: 1 }}>
      {!filesOrLinks && hasPlayableAudio && (
        <Box sx={{ width: 110 }}>
          <PlayButtonOpenPlayer itemId={itemId} hasPlayableAudio={hasPlayableAudio} />
        </Box>
      )}
      <Box sx={{ flexGrow: 1, display: "flex", gap: 1, alignItems: "center", width: "100%" }}>
        {shouldShowAudioTranscriptContent && (
          <>
            <Box sx={{ width: "100%" }}>
              {displayArtifactRecord && (
                <>
                  <Typography component="h3" sx={{ fontWeight: 700, fontSize: "18px", wordBreak: "break-word" }}>
                    {displayArtifactRecord?.title}
                  </Typography>
                  <div
                    id={`${feedItemId}-description`}
                    role="textbox"
                    style={{ wordBreak: "break-word", whiteSpace: "pre-wrap" }}
                    dangerouslySetInnerHTML={{
                      __html: sanitizeHtmlForDisplay(displayArtifactRecord?.description ?? ""),
                    }}
                  />
                </>
              )}

              {shouldShowAudioTranscriptContent && hasTranscription && displayArtifactRecord && !hasCallRecord && (
                <HideShowTranscriptButton
                  mine={mine}
                  displayArtifact={!!displayArtifactRecord?.id}
                  showTranscription={showModalTranscription}
                  onClick={() => setShowModalTranscription(true)}
                />
              )}

              {shouldShowAudioTranscriptContent && hasTranscription && !displayArtifactRecord && !hasCallRecord && (
                <DisplayTranscript
                  mine={mine}
                  feedId={feedId}
                  itemId={itemId}
                  showTranscription={showTranscription}
                  preferredLanguage={preferredLanguage}
                  transcriptions={transcriptions}
                  displayArtifact={!!displayArtifactRecord?.id}
                  feedItemId={feedItemId}
                  transcriptionContent={transcriptionContent}
                  unreliableTranscription={unreliableTranscription}
                  unreliableTranscriptLabel={unreliableTranscriptLabel}
                  files={files}
                  links={links}
                  hasDisplayArtifact={hasDisplayArtifact}
                />
              )}

              {shouldShowAudioTranscriptContent && links?.length > 0 && (
                <List>
                  {links.map((link, index) => (
                    <ListItem key={`links-${link?.id}`} divider={index !== links?.length - 1}>
                      {link?.fragment}
                    </ListItem>
                  ))}
                </List>
              )}

              {shouldShowAudioTranscriptContent && files && files?.length > 0 && (
                <List>
                  {files.map((file) => (
                    <ListItem onClick={() => ampli.openFile()} key={`files-${file?.id}`}>
                      {file?.fragment}
                    </ListItem>
                  ))}
                </List>
              )}

              {transcriptTranslatedFrom && (
                <Box
                  sx={{
                    fontSize: "13px",
                    width: "100%",
                    paddingRight: mine ? 0 : "3rem",
                    color: mine ? theme.palette.info.light : theme.palette.brand.other.gray.main,
                    mt: 2,
                    marginBottom: "-10px",
                  }}
                >
                  Translated from: {transcriptTranslatedFrom}
                </Box>
              )}
            </Box>
          </>
        )}
        {hasCallRecord && (
          <Box sx={{ width: "66.67%", mx: "auto", p: 1 }}>
            <DisplayCallRecord callRecord={callRecords[0]} mine={mine} />
          </Box>
        )}
      </Box>
    </Box>
  );
}
